import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';

@Component({
  selector: 'ca-data-feeder-settings',
  templateUrl: './data-feeder-settings.component.html',
  styleUrls: ['./data-feeder-settings.component.scss'],
  standalone: false,
})
export class DataFeederSettingsComponent implements OnInit, OnDestroy {
  form: FormGroup;
  items = [
    { key: 'DataFeeder.ConversationReady', labelKey: 'Settings::DataFeeder.ConversationReady' },
    { key: 'DataFeeder.AnalysisReady', labelKey: 'Settings::DataFeeder.AnalysisReady' },
    { key: 'DataFeeder.AiInsightsReady', labelKey: 'Settings::DataFeeder.AiInsightsReady' },
    { key: 'DataFeeder.CategoryMatched', labelKey: 'Settings::DataFeeder.CategoryMatched' },
    { key: 'DataFeeder.RedactionReady', labelKey: 'Settings::DataFeeder.RedactionReady' },
    { key: 'DataFeeder.EvaluationReady', labelKey: 'Settings::DataFeeder.EvaluationReady' },
  ];

  receiverTypeItems = [
    { key: 0, labelKey: 'Kafka' },
    { key: 1, labelKey: 'Amazon S3' },
  ];

  triggerEventItems = [
    { key: 'ConversationCreatedForDataFeeder', labelKey: 'Settings::DataFeeder.ConversationCreated' },
    { key: 'ConversationAnalyzedForDataFeeder', labelKey: 'Settings::DataFeeder.ConversationAnalyzed' },
    { key: 'ConversationEvaluatedForDataFeeder', labelKey: 'Settings::DataFeeder.ConversationEvaluated' },
    { key: 'ConversationIndexedForDataFeeder', labelKey: 'Settings::DataFeeder.ConversationIndexed' },
    { key: 'ConversationMaskedForDataFeeder', labelKey: 'Settings::DataFeeder.ConversationMasked' },
    { key: 'QueryIndexCompletedForDataFeeder', labelKey: 'Settings::DataFeeder.QueryIndexCompleted' },
    { key: 'ConversationAiInsightGeneratedForDataFeeder', labelKey: 'Settings::DataFeeder.ConversationAiInsightGenerated' },
  ];

  private readonly RECEIVER_TYPE_SETTING_KEY = 'DataFeeder.ReceiverType';
  private readonly TRIGGER_EVENT_TYPE_SETTING_KEY = 'DataFeeder.TriggerEvent';
  private readonly MIGRATE_MASKED_AUDIO_SETTING_KEY = `DataFeeder.MigrateMaskedAudio`;
  private readonly MASKED_AUDIO_EXTENSION_SETTING_KEY = `DataFeeder.MaskedAudioExtension`;
  migrateMaskedAudioCb = false;
  private destroy$ = new Subject<void>();


  constructor(
    private fb: FormBuilder,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private config: ConfigStateService,
    private configurationSettingsService: ConfigurationSettingsService,
  ) {
    this.migrateMaskedAudioCb = config.getSetting(this.MIGRATE_MASKED_AUDIO_SETTING_KEY) == 'true';
    this.form = this.fb.group({
      settings: [],
      receiverType: [0],
      triggerEvent: [''],
      migrateMaskedAudio: ['false'],
      maskedAudioExtension: ['.mp3']
    });
  }

  ngOnInit(): void {
    const selectedKeys = this.items
      .filter(item => this.config.getSetting(item.key) === 'true')
      .map(item => item.key);

    const savedReceiverType = this.config.getSetting(this.RECEIVER_TYPE_SETTING_KEY);
    const receiverTypeValue =
      savedReceiverType !== undefined && savedReceiverType !== null
        ? Number(savedReceiverType)
        : 0;

    const savedTriggerEvent = this.config.getSetting(this.TRIGGER_EVENT_TYPE_SETTING_KEY);
    const triggerEventValue = savedTriggerEvent ?? '';

    const migrateMaskedAudioValue = this.config.getSetting(this.MIGRATE_MASKED_AUDIO_SETTING_KEY) === 'true';
    const maskedAudioExtensionValue = this.config.getSetting(this.MASKED_AUDIO_EXTENSION_SETTING_KEY) ?? ['.mp3'];

    this.form.patchValue({
      settings: selectedKeys,
      receiverType: receiverTypeValue,
      triggerEvent: triggerEventValue,
      migrateMaskedAudio: migrateMaskedAudioValue,
      maskedAudioExtension: maskedAudioExtensionValue,
    });
  }

  onSubmitForm(): void {
    const selectedKeys = this.form.get('settings').value ?? [];
    const selectedReceiverType: number = this.form.get('receiverType').value ?? 0;
    const selectedTriggerEvent: string = this.form.get('triggerEvent').value ?? '';
    const maskedAudioExtensionValue: string = this.form.get('maskedAudioExtension').value ?? '.mp3';


    const dtos = this.items.map(item =>
      Object.assign(new ConfigurationSettingsDto(), {
        settingName: item.key,
        settingValue: selectedKeys.includes(item.key) ? 'true' : 'false',
      }),
    );

    dtos.push(
      Object.assign(new ConfigurationSettingsDto(), {
        settingName: this.RECEIVER_TYPE_SETTING_KEY,
        settingValue: selectedReceiverType?.toString() ?? '0',
      }),
    );

    dtos.push(
      Object.assign(new ConfigurationSettingsDto(), {
        settingName: this.TRIGGER_EVENT_TYPE_SETTING_KEY,
        settingValue: selectedTriggerEvent ?? '',
      }),
    );

    dtos.push(
      Object.assign(new ConfigurationSettingsDto(), {
        settingName: this.MIGRATE_MASKED_AUDIO_SETTING_KEY,
        settingValue: this.migrateMaskedAudioCb.toString(),
      }),
    );

    dtos.push(
      Object.assign(new ConfigurationSettingsDto(), {
        settingName: this.MASKED_AUDIO_EXTENSION_SETTING_KEY,
        settingValue: maskedAudioExtensionValue,
      }),
    );

    this.configurationSettingsService
      .saveSetting(dtos)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.toastr.success(
          this.localizationService.instant('AbpSettingManagement::SavedSuccessfully'),
        );
      });
  }

  removeFromSelected(key: string): void {
    const selectedItems = this.form.get('settings').value ?? [];
    if (selectedItems) {
      const indexToRemoveFromSelected = selectedItems.findIndex(k => k === key);
      if (indexToRemoveFromSelected > -1) {
        selectedItems.splice(indexToRemoveFromSelected, 1);
        this.form.get('settings').setValue(selectedItems);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}